import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { GET_MEDIA_ALLOWANCE } from '../../graphql';

/**
 * What the site's plan allows for media, and what it has used: storage for
 * images and files, minutes of video kept, and minutes of video watched this
 * month (past the included minutes is billed after the month ends). Over an
 * allowance nothing is removed; new uploads are refused until there's room.
 */

export interface Allowance {
  planLabel: string | null;
  storageUsedBytes: number;
  storageLimitBytes: number | null;
  videoUsedSeconds: number;
  videoLimitSeconds: number | null;
  viewingMonth: string;
  minutesWatched: number;
  includedViewMinutes: number;
  overageMinutes: number;
  overageCentsSoFar: number;
  overageRate: string;
  overStorage: boolean;
  overVideo: boolean;
}

const GB = 1024 ** 3;
const num = (n: number) => Math.round(n).toLocaleString('en-US');
function size(bytes: number) {
  if (bytes < 1024 ** 2) return `${Math.max(0, Math.round(bytes / 1024))} KB`;
  if (bytes < GB) return `${Math.round(bytes / 1024 ** 2)} MB`;
  const g = bytes / GB;
  return `${g >= 10 ? Math.round(g) : g.toFixed(1)} GB`;
}

function Meter({
  label,
  used,
  limit,
  usedText,
  limitText,
  note,
}: {
  label: string;
  used: number;
  limit: number | null;
  usedText: string;
  limitText: string | null;
  note?: string | null;
}) {
  const pct = limit ? Math.min(100, (used / limit) * 100) : limit === 0 ? 100 : 0;
  const over = limit != null && used >= limit && (limit > 0 || used > 0);
  const color = over ? 'var(--ink-red)' : pct >= 80 ? 'var(--ink-amber)' : 'var(--ink-accent)';
  return (
    <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 13 }}>
        <span style={{ color: 'var(--ink-ink)', fontWeight: 600 }}>{label}</span>
        <span style={{ color: 'var(--ink-ink3)', whiteSpace: 'nowrap' }}>
          {limitText === null ? usedText : `${usedText} of ${limitText}`}
        </span>
      </div>
      {limit !== null && (
        <div
          role="progressbar"
          aria-label={label}
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{ height: 6, borderRadius: 999, background: 'var(--ink-bg3)', overflow: 'hidden' }}
        >
          <div style={{ width: `${pct}%`, height: '100%', background: color }} />
        </div>
      )}
      {note && (
        <span style={{ fontSize: 12, color: over ? 'var(--ink-red)' : 'var(--ink-ink3)' }}>
          {note}
        </span>
      )}
    </div>
  );
}

export function useMediaAllowance(accountId: string | null | undefined) {
  const { data, refetch } = useQuery(GET_MEDIA_ALLOWANCE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  return { allowance: (data?.mediaAllowance as Allowance | undefined) ?? null, refetch };
}

export function MediaAllowanceMeters({ allowance }: { allowance: Allowance }) {
  const a = allowance;
  const month = new Date(`${a.viewingMonth}T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });
  const videoMinutes = a.videoLimitSeconds == null ? null : Math.round(a.videoLimitSeconds / 60);
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))',
        gap: 16,
      }}
    >
      <Meter
        label="Storage"
        used={a.storageUsedBytes}
        limit={a.storageLimitBytes}
        usedText={size(a.storageUsedBytes)}
        limitText={a.storageLimitBytes == null ? null : size(a.storageLimitBytes)}
        note={a.overStorage ? 'Full — new uploads are paused until there’s room.' : null}
      />
      <Meter
        label="Video"
        used={a.videoUsedSeconds}
        limit={a.videoLimitSeconds}
        usedText={`${num(a.videoUsedSeconds / 60)} min`}
        limitText={videoMinutes == null ? null : `${num(videoMinutes)} min`}
        note={
          videoMinutes === 0
            ? 'Your plan doesn’t include video.'
            : a.overVideo
              ? 'No minutes left — delete a video or upgrade to add more.'
              : null
        }
      />
      {(a.includedViewMinutes > 0 || a.minutesWatched > 0) && (
        <Meter
          label={`Watched in ${month}`}
          used={a.minutesWatched}
          limit={a.includedViewMinutes}
          usedText={`${num(a.minutesWatched)} min`}
          limitText={`${num(a.includedViewMinutes)} included`}
          note={
            a.overageMinutes > 0
              ? `${num(a.overageMinutes)} min over — about $${(a.overageCentsSoFar / 100).toFixed(2)} so far (${a.overageRate.replace(/ over your plan$/, '')}), billed after the month ends.`
              : null
          }
        />
      )}
    </div>
  );
}

/** The meters with an upgrade link when something is full — top of the library. */
export function MediaAllowanceBar({ allowance }: { allowance: Allowance | null }) {
  if (!allowance) return null;
  const full = allowance.overStorage || allowance.overVideo;
  return (
    <div
      className="ink-site-card"
      style={{ padding: 14, display: 'grid', gap: 10, marginBottom: 12, flex: 'none' }}
    >
      <MediaAllowanceMeters allowance={allowance} />
      {full && (
        <Link to="/settings/billing" style={{ fontSize: 13, color: 'var(--ink-accent)' }}>
          See plans with more room →
        </Link>
      )}
    </div>
  );
}
